/**
 * Layout HTML institucional para todos los correos del PMIS.
 *
 * Estilo inline (los clientes de correo no procesan CSS externo). Look:
 * cabecera Tecpetrol azul oscuro, cuerpo blanco, CTA azul, pie gris
 * con disclaimer. Tabla outer de 600px (estándar de email) centrada.
 *
 * Las funciones aquí son puras y no dependen del DOM; pueden ejecutarse
 * en cualquier contexto (browser, tests, server-side si en algún momento
 * se mueve a un Function App).
 */

import type { MailRendered } from "./mailContext";

export interface LayoutArgs {
  /** Banner superior — handoverId + módulo (ej. "PR-2 · Purchase Requisitions"). */
  banner: string;
  /** Título grande del correo. */
  titulo: string;
  /** Cuerpo HTML — párrafos, listas, tablas. Inyectado tal cual. */
  cuerpo: string;
  /** Texto del botón principal. */
  ctaLabel: string;
  /** URL del botón principal. */
  ctaUrl: string;
  /** Texto pequeño debajo del CTA (opcional). */
  ctaHint?: string;
}

/**
 * Construye el HTML completo del correo.
 *
 * Diseño: tabla 600px centrada → cabecera azul Tecpetrol → cuerpo
 * blanco con padding → CTA azul → pie gris con disclaimer y enlace al
 * PMIS. Todo inline-styled para máxima compatibilidad (Outlook desktop,
 * Outlook web, Gmail, Apple Mail, móviles).
 */
export function renderLayout(args: LayoutArgs): string {
  return `<!DOCTYPE html>
<html lang="es-CO">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escape(args.titulo)}</title>
</head>
<body style="margin:0;padding:0;background:#F3F4F6;font-family:'Segoe UI',Roboto,Arial,sans-serif;color:#1F2937;">
<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background:#F3F4F6;padding:24px 0;">
  <tr>
    <td align="center">
      <table role="presentation" cellpadding="0" cellspacing="0" width="600" style="max-width:600px;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.06);">
        <tr>
          <td style="background:#003A70;padding:18px 24px;color:#ffffff;">
            <div style="font-size:11px;letter-spacing:0.06em;text-transform:uppercase;opacity:0.85;">${escape(args.banner)}</div>
            <div style="font-size:13px;font-weight:600;margin-top:2px;">PMIS — Bloque CPO-13 · Tecpetrol Colombia</div>
          </td>
        </tr>
        <tr>
          <td style="padding:28px 28px 8px 28px;">
            <h1 style="margin:0 0 16px 0;font-size:20px;color:#003A70;font-weight:600;line-height:1.3;">${escape(args.titulo)}</h1>
            <div style="font-size:14px;line-height:1.55;color:#1F2937;">${args.cuerpo}</div>
          </td>
        </tr>
        <tr>
          <td align="center" style="padding:8px 28px 28px 28px;">
            <a href="${escape(args.ctaUrl)}" style="display:inline-block;background:#003A70;color:#ffffff;text-decoration:none;font-weight:600;font-size:14px;padding:11px 22px;border-radius:6px;">${escape(args.ctaLabel)}</a>
            ${
              args.ctaHint
                ? `<div style="margin-top:8px;font-size:11px;color:#6B7280;">${escape(args.ctaHint)}</div>`
                : ""
            }
          </td>
        </tr>
        <tr>
          <td style="background:#F9FAFB;border-top:1px solid #E5E7EB;padding:14px 24px;font-size:11px;color:#6B7280;line-height:1.5;">
            Este correo lo envió automáticamente el PMIS. Si crees que lo recibiste por error
            o quieres ajustar qué notificaciones recibes, contacta al Administrador del PMIS.
            <br><br>
            <a href="${escape(args.ctaUrl)}" style="color:#003A70;text-decoration:none;">Abrir PMIS</a>
            &nbsp;·&nbsp;
            <span>Tecpetrol Colombia S.A.</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
}

/**
 * Construye una tabla resumen 2 columnas (campo / valor). Útil para
 * mostrar datos de la entidad sin formularlos en prosa.
 */
export function tablaResumen(filas: Array<[string, string]>): string {
  const rows = filas
    .map(
      ([k, v]) => `
    <tr>
      <td style="padding:6px 12px 6px 0;color:#6B7280;font-size:13px;white-space:nowrap;vertical-align:top;">${escape(k)}</td>
      <td style="padding:6px 0;color:#1F2937;font-size:13px;vertical-align:top;">${escape(v)}</td>
    </tr>`,
    )
    .join("");
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:8px 0 16px 0;border-collapse:collapse;">${rows}</table>`;
}

/** Párrafo con énfasis tipo "callout". */
export function callout(texto: string, color: "info" | "warning" | "danger" = "info"): string {
  const colores = {
    info: { bg: "#EAF3FB", border: "#BFD7EA", fg: "#1F4E79" },
    warning: { bg: "#FFF8E7", border: "#F0D78C", fg: "#8A6500" },
    danger: { bg: "#FDECEC", border: "#F1B0B0", fg: "#A23B3B" },
  }[color];
  return `<div style="background:${colores.bg};border-left:3px solid ${colores.border};color:${colores.fg};padding:10px 14px;border-radius:4px;margin:12px 0;font-size:13px;line-height:1.5;">${escape(texto)}</div>`;
}

/** Construye {subject, html} aplicando el layout. Helper para los templates. */
export function aplicarLayout(args: LayoutArgs & { asunto: string }): MailRendered {
  return {
    subject: args.asunto,
    html: renderLayout(args),
  };
}

/**
 * Escapa caracteres HTML peligrosos. NO usar para inyectar HTML
 * pre-renderizado (como `cuerpo` arriba); usar solo para strings de
 * usuario / datos que pueden contener `<`, `&`, etc.
 */
function escape(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
